const initialState = {
  userInfo: {}
};

// Around a hundred call sites read permissions off `userInfo.user.*` with
// optional chaining — `erpType` alone in ~55 places, plus poMatchPermission,
// the construction-company flag, deliveryTicketUpload and the rest — and then use the
// result as a boolean. So a payload arriving without `user` does not throw:
// every permission quietly becomes `undefined`, which reads as "denied", and
// the UI hides features instead of reporting that it never loaded the profile.
// Treat such a payload as no answer at all and keep the last good profile.
// Gates on `user._id` only, deliberately.
//
// A payload with `user` but `company: null` (Tier B returns that whenever the
// company lookup misses) does strip every `userDetails.company.*` permission —
// the same disease. But rejecting it is worse: if the condition is persistent,
// every retry is rejected identically, and an account with no last-good profile
// to fall back on is left with `userInfo === {}` permanently — no permissions
// at all and an undismissable banner, where before it at least had a partial
// profile. A guard against silent degradation must not itself be able to lock
// someone out. The company-null case needs handling at its source instead.
export const isUsableProfile = (profile) => Boolean(profile?.user?._id);

// The same disease as a missing `user`, one level along, and the one that hit
// Frank M. Booth on 2026-08-13: a payload carrying a perfectly good `user._id`
// but `role: ''`. Every gate in the app reads
// `rolePermissions?.[userDetails.role]?.someAction`, so an empty role makes all
// of them `undefined` — denied — while `isUsableProfile` happily certifies the
// payload as good. Nothing retried, nothing warned, and the screen rendered as
// though the user simply had no permissions.
//
// Tier B no longer emits this (it now refuses the request outright), but the
// predicate stays because the store must not depend on that being true of every
// deployed backend, and a persisted profile from before the fix can still carry
// an empty role.
export const hasUsableRole = (profile) =>
  typeof profile?.role === 'string' && profile.role.trim() !== '';

// What "we know who this user is" means for the fetch/retry/banner machinery:
// stored AND able to authorise. `isUsableProfile` alone is the storage rule and
// is deliberately laxer — see the company-null note above for why a storage
// gate must never be able to lock anyone out. This one decides whether to keep
// asking, which is free.
export const isCompleteProfile = (profile) =>
  isUsableProfile(profile) && hasUsableRole(profile);

const UserInformationReducer = (state = initialState, action) => {
  switch (action.type) {
    case 'userInfo':
      // Reject outright — never merge. Laying a partial payload over the old
      // profile is exactly the cross-tenant bleed the full replace below was
      // introduced to stop.
      if (!isUsableProfile(action.payload)) {
        return state;
      }
      // Never trade a profile that can authorise for one that cannot. This is
      // deliberately conditional rather than a flat rejection: a flat one would
      // repeat the company-null mistake above and leave a fresh login — which
      // has no last-good profile — with `userInfo === {}` for good. Here, a
      // session that already knows its role keeps it, and a session that never
      // had one still gets the payload, with App.js retrying and warning on top.
      if (!hasUsableRole(action.payload) && hasUsableRole(state.userInfo)) {
        return state;
      }
      return {
        ...state,
        // Full replace, not merge: merging left the previous account's company
        // and permission fields in place wherever the new payload didn't
        // overwrite them.
        userInfo: action.payload
      };
    default:
      return state;
  }
};
export default UserInformationReducer;
